if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');
        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.isSubmitting = false;
        this.error = false;

        if (!this.form || !this.submitButton) return;

        this.variantIdInput = this.form.querySelector('[name="id"]');

        if (this.variantIdInput) {
          this.variantIdInput.disabled = false;
        }

        this.form.addEventListener(
          'submit',
          this.onSubmitHandler.bind(this)
        );

        if (this.cart) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();

        // Prevent double-clicks / duplicate requests
        if (this.isSubmitting) return;

        // Make sure the form exists
        if (!this.form || !this.submitButton) return;

        // Get variant ID
        const variantIdInput = this.form.querySelector('[name="id"]');
        const variantId = variantIdInput?.value;

        if (!variantId) {
          console.error('Add to cart failed: Variant ID is missing.');
          this.handleErrorMessage('Please select a product option.');
          return;
        }

        this.isSubmitting = true;
        this.error = false;

        this.handleErrorMessage();

        // Lock button
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.disabled = true;
        this.submitButton.classList.add('loading');

        const spinner = this.querySelector('.loading__spinner');
        if (spinner) {
          spinner.classList.remove('hidden');
        }

        try {
          const config = fetchConfig('javascript');

          config.headers['X-Requested-With'] = 'XMLHttpRequest';

          delete config.headers['Content-Type'];

          const formData = new FormData(this.form);

          // Always make sure quantity exists
          if (!formData.get('quantity')) {
            formData.set('quantity', '1');
          }

          if (this.cart) {
            const sections = this.cart
              .getSectionsToRender()
              .map((section) => section.id);

            formData.append('sections', sections);
            formData.append('sections_url', window.location.pathname);

            this.cart.setActiveElement(document.activeElement);
          }

          config.body = formData;

          const quantity =
            parseInt(formData.get('quantity'), 10) || 1;

          const linesUpdateDeferred =
            this.createCartLinesUpdateEvent(
              variantId,
              quantity
            );

          const response = await fetch(
            `${routes.cart_add_url}`,
            config
          );

          // Handle HTTP errors
          if (!response.ok) {
            throw new Error(
              `Add to cart failed (${response.status})`
            );
          }

          const responseData = await response.json();

          // Shopify returned an error
          if (responseData.status) {
            this.error = true;

            const errorMessage =
              responseData.description ||
              responseData.message ||
              'Unable to add this item to your cart.';

            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: variantId,
              errors: responseData.errors || errorMessage,
              message: errorMessage,
            });

            this.handleErrorMessage(errorMessage);

            this.dispatchCartErrorEvent(
              errorMessage,
              'INVALID'
            );

            linesUpdateDeferred?.reject(
              new Error(errorMessage)
            );

            return;
          }

          // No cart drawer/notification
          if (!this.cart) {
            this.resolveCartLinesUpdate(
              linesUpdateDeferred
            );

            window.location.href = window.routes.cart_url;
            return;
          }

          this.resolveCartLinesUpdate(
            linesUpdateDeferred
          );

          // Successful add
          this.error = false;

          const startMarker =
            CartPerformance.createStartingMarker(
              'add:wait-for-subscribers'
            );

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: variantId,
            cartData: responseData,
          }).then(() => {
            CartPerformance.measureFromMarker(
              'add:wait-for-subscribers',
              startMarker
            );
          });

          const quickAddModal =
            this.closest('quick-add-modal');

          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => {
                setTimeout(() => {
                  if (!this.cart) return;

                  CartPerformance.measure(
                    'add:paint-updated-sections',
                    () => {
                      this.cart.renderContents(
                        responseData
                      );
                    }
                  );
                }, 0);
              },
              { once: true }
            );

            quickAddModal.hide(true);
          } else {
            CartPerformance.measure(
              'add:paint-updated-sections',
              () => {
                if (this.cart) {
                  this.cart.renderContents(
                    responseData
                  );
                }
              }
            );
          }
        } catch (error) {
          console.error(
            'Noor Al Haya Add to Cart Error:',
            error
          );

          this.error = true;

          const message =
            error?.message ||
            'Unable to add the product to cart. Please try again.';

          this.handleErrorMessage(message);

          this.dispatchCartErrorEvent(
            message,
            'SERVICE_UNAVAILABLE'
          );
        } finally {
          this.isSubmitting = false;

          this.submitButton.classList.remove('loading');

          const spinner =
            this.querySelector('.loading__spinner');

          if (spinner) {
            spinner.classList.add('hidden');
          }

          if (
            this.cart &&
            this.cart.classList.contains('is-empty') &&
            !this.error
          ) {
            this.cart.classList.remove('is-empty');
          }

          // IMPORTANT:
          // Always restore the button after request finishes
          this.submitButton.removeAttribute(
            'aria-disabled'
          );

          this.submitButton.disabled = false;

          if (this.error) {
            this.submitButtonText &&
              (this.submitButtonText.textContent =
                window.variantStrings.addToCart);
          }

          CartPerformance.measureFromEvent(
            'add:user-action',
            evt
          );
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(
            '.product-form__error-message-wrapper'
          );

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute(
          'hidden',
          !errorMessage
        );

        if (errorMessage && this.errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;

        if (disable) {
          this.submitButton.setAttribute(
            'disabled',
            'disabled'
          );

          if (text && this.submitButtonText) {
            this.submitButtonText.textContent = text;
          }
        } else {
          this.submitButton.removeAttribute(
            'disabled'
          );

          if (this.submitButtonText) {
            this.submitButtonText.textContent =
              window.variantStrings.addToCart;
          }
        }
      }

      createCartLinesUpdateEvent(
        variantId,
        quantity
      ) {
        const { CartLinesUpdateEvent } =
          window.StandardEvents || {};

        if (!CartLinesUpdateEvent) return null;

        const deferred =
          CartLinesUpdateEvent.createPromise();

        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [
              {
                merchandiseId: variantId,
                quantity,
              },
            ],
            promise: deferred.promise,
          })
        );

        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;

        const { CartLinesUpdateEvent } =
          window.StandardEvents || {};

        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise =
          typeof CartItems !== 'undefined'
            ? CartItems.fetchCartData()
            : fetch(`${routes.cart_url}.json`).then(
                (response) => response.json()
              );

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) {
              deferred.reject(
                new Error(
                  'Missing currency in cart response'
                )
              );
              return;
            }

            deferred.resolve({
              cart:
                CartLinesUpdateEvent.createCartFromAjaxResponse(
                  cart
                ),
            });
          })
          .catch((error) => {
            deferred.reject(error);
          });
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } =
          window.StandardEvents || {};

        if (!CartErrorEvent) return;

        this.dispatchEvent(
          new CartErrorEvent({
            error: message,
            code,
          })
        );
      }

      get variantIdInput() {
        return this.form?.querySelector('[name="id"]');
      }
    }
  );
}